import React, { useRef, useState } from "react";
import { tasksAPI } from "../../api/client";
import { formatTimestamp } from "./taskUtils";
import useConfirm from "./ConfirmDialog";

const MAX_BYTES = 25 * 1024 * 1024;
// Keep in sync with backend/src/utils/taskFiles.js.
const ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,application/pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip";

export function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileBadge(attachment) {
  if (attachment.mimeType.startsWith("image/")) return "IMG";
  const ext = attachment.fileName.split(".").pop();
  return ext && ext !== attachment.fileName ? ext.slice(0, 4).toUpperCase() : "FILE";
}

// "Attachments" block in the task pane: upload by picking or dropping files,
// open or download each one, and remove your own.
export default function AttachmentSection({ task, onUpload, onRemove }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [formError, setFormError] = useState("");
  const [confirm, confirmDialog] = useConfirm();

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const tooBig = list.filter((f) => f.size > MAX_BYTES);
    setFormError(
      tooBig.length
        ? `${tooBig.map((f) => f.name).join(", ")} ${tooBig.length === 1 ? "is" : "are"} over 25 MB.`
        : "",
    );
    // One at a time, so the pane refreshes after each and a failure stops
    // only the files after it.
    for (const file of list.filter((f) => f.size <= MAX_BYTES)) {
      setUploading((n) => n + 1);
      const ok = await onUpload(file);
      setUploading((n) => n - 1);
      if (!ok) break;
    }
  };

  const attachments = task.attachments || [];

  return (
    <div
      className={`tk-section tk-attach-section${dragging ? " is-dragging" : ""}`}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        upload(e.dataTransfer.files);
      }}
    >
      <h3 className="tk-section-title">
        Attachments
        {attachments.length > 0 && (
          <span className="tk-count">{attachments.length}</span>
        )}
        <button
          type="button"
          className="tk-link-btn tk-time-add"
          disabled={uploading > 0}
          onClick={() => inputRef.current?.click()}
        >
          {uploading > 0 ? "Uploading…" : "+ Add file"}
        </button>
      </h3>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        hidden
        onChange={(e) => {
          upload(e.target.files);
          e.target.value = "";
        }}
      />

      {formError && <p className="tk-error">{formError}</p>}

      {attachments.length > 0 ? (
        <ul className="tk-attach-list">
          {attachments.map((a) => (
            <li key={a.id}>
              <span className="tk-attach-badge">{fileBadge(a)}</span>
              <span className="tk-attach-info">
                <a
                  className="tk-attach-name"
                  href={tasksAPI.attachmentUrl(a.filePath)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={a.fileName}
                >
                  {a.fileName}
                </a>
                <span className="tk-attach-meta">
                  {formatBytes(a.size)} · {a.uploadedBy?.name} ·{" "}
                  {formatTimestamp(a.createdAt)}
                </span>
              </span>
              <a
                className="tk-icon-btn tk-icon-btn-sm"
                href={tasksAPI.attachmentUrl(a.filePath, { download: true })}
                aria-label={`Download ${a.fileName}`}
                title="Download"
              >
                ↓
              </a>
              {a.canDelete && (
                <button
                  type="button"
                  className="tk-icon-btn tk-icon-btn-sm"
                  onClick={async () => {
                    const ok = await confirm({
                      title: "Remove this attachment?",
                      subject: a.fileName,
                      message: "It will be deleted for everyone on this task.",
                      confirmLabel: "Remove attachment",
                    });
                    if (ok) onRemove(a.id);
                  }}
                  aria-label={`Remove ${a.fileName}`}
                  title="Remove"
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="tk-muted">
          {uploading > 0 ? "Uploading…" : "No files yet — drop files here or use + Add file."}
        </p>
      )}
      {confirmDialog}
    </div>
  );
}
